import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone, ChevronDown, MessageCircle } from "lucide-react";
import { FIRM_NAME, PHONE, WHATSAPP_LINK } from "@/data";
import { Link, useNavigate } from "react-router-dom";

const navLinks = [
  { label: "Home", href: "#home", type: "scroll" },
  { label: "Services", href: "#services", type: "scroll" },
  { label: "Process", href: "#process", type: "scroll" },
  { label: "FAQ", href: "#faq", type: "scroll" },
  { label: "Articles", href: "/articles", type: "link" },
  { label: "Contact", href: "#contact", type: "scroll" },
];

export function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handler);
    return () => window.removeEventListener("scroll", handler);
  }, []);

  const handleNav = (item: (typeof navLinks)[0]) => {
    setMobileOpen(false);
    if (item.type === "link") {
      navigate(item.href);
      return;
    }
    // If not on home page, go home first then scroll
    const el = document.querySelector(item.href);
    if (el) {
      el.scrollIntoView({ behavior: "smooth" });
    } else {
      navigate("/");
      setTimeout(() => {
        document.querySelector(item.href)?.scrollIntoView({ behavior: "smooth" });
      }, 300);
    }
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-primary shadow-lg" : "bg-primary/95"
      }`}
    >
      <nav className="container mx-auto px-4 flex items-center justify-between h-16 md:h-20">
        {/* Logo */}
        <Link to="/" className="flex flex-col leading-tight">
          <span
            className="text-primary-foreground font-bold text-lg md:text-xl tracking-tight"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            {FIRM_NAME}
          </span>
          <span className="text-primary-foreground/60 text-xs hidden md:block">
            Civil & Criminal Claim Trials · Singapore
          </span>
        </Link>

        {/* Desktop nav */}
        <div className="hidden lg:flex items-center gap-6">
          {navLinks.map((l) => (
            <button
              key={l.href}
              onClick={() => handleNav(l)}
              className="text-primary-foreground/80 hover:text-primary-foreground text-sm font-medium transition-colors"
            >
              {l.label}
            </button>
          ))}
        </div>

        {/* CTA */}
        <div className="hidden md:flex items-center gap-3">
          <a
            href={`tel:${PHONE.replace(/\s/g, "")}`}
            className="flex items-center gap-2 text-primary-foreground/80 hover:text-primary-foreground text-sm transition-colors"
          >
            <Phone size={15} />
            {PHONE}
          </a>
          <a
            href={WHATSAPP_LINK}
            target="_blank"
            rel="noopener noreferrer"
            className="bg-accent text-sm font-bold px-4 py-2 rounded-md hover:bg-accent/90 transition-colors"
            style={{ color: "#0a1628" }}
          >
            Free Consultation
          </a>
        </div>

        {/* Mobile hamburger */}
        <button
          className="lg:hidden text-primary-foreground p-2"
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label="Toggle menu"
        >
          {mobileOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </nav>

      {/* Mobile menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-primary border-t border-primary-foreground/10"
          >
            <div className="container mx-auto px-4 py-4 flex flex-col gap-3">
              {navLinks.map((l) => (
                <button
                  key={l.href}
                  onClick={() => handleNav(l)}
                  className="text-primary-foreground/80 hover:text-primary-foreground text-sm font-medium text-left py-2 border-b border-primary-foreground/10 transition-colors"
                >
                  {l.label}
                </button>
              ))}
              <a
                href={WHATSAPP_LINK}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-2 bg-accent text-center py-3 rounded-md text-sm font-bold"
                style={{ color: "#0a1628" }}
              >
                Free Consultation
              </a>
              <a
                href={`tel:${PHONE.replace(/\s/g, "")}`}
                className="text-center text-primary-foreground/70 text-sm"
              >
                Call: {PHONE}
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}

export function Footer() {
  const navigate = useNavigate();

  const handleNav = (item: (typeof navLinks)[0]) => {
    if (item.type === "link") {
      navigate(item.href);
      return;
    }
    const el = document.querySelector(item.href);
    if (el) {
      el.scrollIntoView({ behavior: "smooth" });
    } else {
      navigate("/");
      setTimeout(() => {
        document.querySelector(item.href)?.scrollIntoView({ behavior: "smooth" });
      }, 300);
    }
  };

  return (
    <footer className="bg-primary text-primary-foreground">
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
          {/* Brand */}
          <div>
            <h3
              className="text-xl font-bold mb-3"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              {FIRM_NAME}
            </h3>
            <p className="text-primary-foreground/60 text-sm leading-relaxed">
              Singapore's dedicated civil and criminal claim trial lawyers.
              Trusted, experienced, and results-driven.
            </p>
          </div>

          {/* Quick links */}
          <div>
            <h4 className="font-semibold mb-4 text-accent">Quick Links</h4>
            <ul className="space-y-2 text-sm">
              {navLinks.map((l) => (
                <li key={l.href}>
                  <button
                    onClick={() => handleNav(l)}
                    className="text-primary-foreground/60 hover:text-primary-foreground transition-colors"
                  >
                    {l.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact / NAP */}
          <div itemScope itemType="https://schema.org/LegalService">
            <h4 className="font-semibold mb-4 text-accent">Contact Us</h4>
            <address className="not-italic text-sm text-primary-foreground/70 space-y-2">
              <p itemProp="name" className="sr-only">
                {FIRM_NAME}
              </p>
              <p>
                📍{" "}
                <span itemProp="address">Carpenter Street, Singapore</span>
              </p>
              <p>
                📞{" "}
                <a
                  href="tel:+6580148989"
                  itemProp="telephone"
                  className="hover:text-primary-foreground transition-colors"
                >
                  {PHONE}
                </a>
              </p>
              <p>🕐 Mon–Fri: 11am–8pm | Weekends: By Appointment</p>
            </address>
          </div>
        </div>

        <div className="border-t border-primary-foreground/10 mt-10 pt-6 flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-primary-foreground/40">
          <p>
            © {new Date().getFullYear()} {FIRM_NAME}. All rights reserved.
          </p>
          <p>Civil & Criminal Claim Trial Lawyers · Singapore</p>
        </div>
      </div>
    </footer>
  );
}

export function FloatingCTA() {
  return (
    <a
      href={WHATSAPP_LINK}
      target="_blank"
      rel="noopener noreferrer"
      className="fixed bottom-6 right-6 z-50 bg-green-500 text-white w-14 h-14 rounded-full flex items-center justify-center shadow-xl hover:bg-green-600 transition-all duration-200 hover:scale-110"
      aria-label="WhatsApp us"
      title="Chat on WhatsApp"
    >
      <MessageCircle size={26} />
    </a>
  );
}
